import { BodyText, Collapsible } from "@/components/ui";
import { formatPhoneNumber } from "@/lib/formatters";
import { MedicationRequest } from "@curai/js-fhir-tools";
import type * as fhirTypes from "fhir/r4";
import { ClipboardPlus } from "lucide-react";

interface MedicationProps {
  medication: fhirTypes.MedicationRequest;
}

export const Medication = ({ medication }: MedicationProps) => {
  const medicationRequest = new MedicationRequest(medication);

  const renderPharmacy = () => {
    if (!medicationRequest?.pharmacy) {
      return (
        <BodyText tag="p" size="sm">
          None
        </BodyText>
      );
    }

    const location = medicationRequest?.pharmacy;
    const { city, line, postalCode, state } = location.address || {};
    const [phone] = location.telecom || [];

    return (
      <div>
        <BodyText tag="p" size="sm">
          {location.name}
        </BodyText>
        <BodyText tag="p" size="sm">
          {location.description}
        </BodyText>
        <BodyText tag="p" size="sm">
          {line}
        </BodyText>
        <BodyText tag="p" size="sm">
          {city},{state} {postalCode}
        </BodyText>
        <BodyText size="sm">{formatPhoneNumber(phone?.value)}</BodyText>
      </div>
    );
  };

  const renderDirections = () => (
    <div>
      {medicationRequest.dosageInstruction?.map(({ patientInstruction }) => (
        <BodyText tag="p" size="sm" key={patientInstruction}>
          {patientInstruction}
        </BodyText>
      ))}
    </div>
  );

  const groups = [
    {
      title: "Override Reason",
      text: medicationRequest.overrideReason || "N/A",
    },
    {
      title: "Directions For Patient",
      children: renderDirections(),
    },
    {
      title: "Dispense Amount",
      text: `${medicationRequest.dispenseQuantity} ${medicationRequest.dispenseUnitDisplay}`,
    },
    {
      title: "Days Of Supply",
      text: `${medicationRequest.daysSupply} ${medicationRequest.supplyUnit}`,
    },
    {
      title: "Refills",
      text: `${medicationRequest.refills || "N/A"}`,
    },
    {
      title: "Notes For Pharmacy",
      text: medicationRequest.noteToPharmacy,
    },
    {
      title: "Substitutions Allowed",
      text: medicationRequest.substitutionAllowed ? "Yes" : "No",
    },
    {
      title: "Routing Method",
      text: medicationRequest.routingMethod,
    },
    {
      title: "eRx Status",
      text: medicationRequest.dosespotStatusLabel,
    },
    {
      title: "eRx Order ID",
      text: medicationRequest.orderId,
    },
    {
      title: "Pharmacy",
      children: renderPharmacy(),
    },
  ];

  return (
    <Collapsible
      title={medicationRequest.displayName}
      headerSize="sm"
      status={medicationRequest.dosespotStatusLabel}
    >
      <div className="flex flex-col gap-2">
        {groups.map(({ title, children, text }) => (
          <div className="flex flex-col gap-1" key={title}>
            <BodyText tag="p" size="sm" bold>
              {title}
            </BodyText>
            {children || (
              <BodyText tag="p" size="sm">
                {text}
              </BodyText>
            )}
          </div>
        ))}

        <div className="flex items-center gap-1">
          <ClipboardPlus size={12} />
          <BodyText tag="p" size="xs" textColor="secondary">
            Prescribed by: {medicationRequest.requester?.display}
          </BodyText>
        </div>
      </div>
    </Collapsible>
  );
};
